import React, { useState } from 'react';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiCall } from '../../utils/api';

const STATUS_TONE = {
  approved: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  pending: 'bg-amber-50 text-amber-600 border-amber-200',
  info_requested: 'bg-blue-50 text-blue-600 border-blue-200',
  rejected: 'bg-red-50 text-red-600 border-red-200',
  suspended: 'bg-gray-100 text-gray-500 border-gray-200',
};
const ACTIONS = [
  { id: 'approve', label: 'Approve', needsReason: false },
  { id: 'request_info', label: 'Request info', needsReason: true },
  { id: 'suspend', label: 'Suspend', needsReason: true },
  { id: 'reject', label: 'Reject', needsReason: true },
  { id: 'reactivate', label: 'Reactivate', needsReason: false },
];

/** Phase A Sec 4/5 — org roster verification (employer + agency, same API). */
export const RosterVerificationControl = ({ workerId, workerName }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState('');
  const [notOnRoster, setNotOnRoster] = useState(false);

  const toggle = async () => {
    if (!open && status === null) {
      try {
        const d = await apiCall(`/api/org/workers/${workerId}/verification`);
        setStatus(d.verification_status);
        setHistory(d.history || []);
      } catch (e) {
        setNotOnRoster(true);
      }
    }
    setOpen(!open);
  };

  const act = async (action) => {
    const def = ACTIONS.find((a) => a.id === action);
    let reason = '';
    if (def.needsReason) {
      reason = window.prompt(`Reason to ${def.label.toLowerCase()} ${workerName || 'this worker'}:`) || '';
      if (!reason.trim()) return;
    }
    setBusy(action);
    try {
      const d = await apiCall(`/api/org/workers/${workerId}/verification`, {
        method: 'POST',
        body: JSON.stringify({ action, reason }),
      });
      setStatus(d.verification_status);
      setHistory((prev) => [...prev, d.entry]);
      toast.success(`Verification: ${d.verification_status.replace('_', ' ')}`);
    } catch (e) {
      toast.error(e?.message || 'Verification action failed');
    } finally { setBusy(''); }
  };

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={toggle}
        className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border ${STATUS_TONE[status] || 'bg-white text-gray-500 border-gray-200'} hover:opacity-80`}
        data-testid={`roster-verification-btn-${workerId}`}
        title="Roster verification"
      >
        <ShieldCheck className="w-3.5 h-3.5" />
        {status ? status.replace('_', ' ') : 'Verify'}
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-20 w-56 bg-white border border-gray-200 rounded-xl shadow-lg p-2" data-testid={`roster-verification-popover-${workerId}`}>
          {notOnRoster ? (
            <p className="text-xs text-gray-400 px-2 py-2">Worker is not on your roster.</p>
          ) : (
            <>
              <p className="text-[11px] text-gray-400 px-1 pb-1">
                Status: <b className="text-gray-600">{(status || '—').replace('_', ' ')}</b> · {history.length} record{history.length === 1 ? '' : 's'}
              </p>
              {ACTIONS.map((a) => (
                <button
                  key={a.id}
                  onClick={() => act(a.id)}
                  disabled={!!busy}
                  className="w-full text-left text-xs px-2 py-1.5 rounded-lg hover:bg-gray-50 text-gray-700 flex items-center gap-1.5 disabled:opacity-40"
                  data-testid={`roster-verify-${a.id}-${workerId}`}
                >
                  {busy === a.id && <Loader2 className="w-3 h-3 animate-spin" />}
                  {a.label}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default RosterVerificationControl;
